import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { View, XStack, YStack } from "tamagui";
import { RiArrowLeftSLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import TextCustom, { TextSize } from "@components/TextCoustom";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";

/**
 * Skin V2 的頁面骨架（給不在 PageWrapper 底下的獨立路由用）。
 *
 * 為什麼要抽出來：V2 的骨架原本只長在 PageWrapper 裡（見 PageWrapper.tsx 的 gasHeaderV2 分支），
 * 但帳號區有近三十支頁面是 router 的頂層路由、沒有 PageWrapper，於是各自用舊的
 * 「藍色圓角 Header + RadiusContentWrapper」。要換皮就得有一份共用骨架，
 * 否則等於在每支頁面手抄一次漸層與釘住標題列的細節（那些細節很容易抄歪，見下方註解）。
 *
 * 三個關鍵細節，改動時請一併看 all.scss 的 .gas-skin-v2-*：
 * 1. 漸層同時掛在頁面容器與標題列的 ::before，且色停點完全一致——兩者以容器頂端為原點，
 *    才不會在交界浮出一條 1px 色差的橫線。
 * 2. 標題列是 position: absolute（脫離文件流），捲動時不改變任何元素高度＝不重排。
 * 3. 內容區的上內距＝實測的標題列高度，不寫死：字級與安全區會變動。
 */
interface SkinV2ScreenProps {
  /** 釘住的標題列文字（白字、小字級）。 */
  title: string;
  /** 標題列下方、跟著內容一起捲走的區塊（大標、摘要等，落在淡霧頁首上）。 */
  subHeader?: ReactNode;
  /** 標題列右側（如「編輯」等次要動作）。 */
  headerRight?: ReactNode;
  /**
   * 保留參數但已無作用：頁首從四種長度的實藍漸層換成單一的淡藍霧（見 all.scss 的 .gas-skin-v2-bg），
   * 淡霧不必配合 subHeader 的高度伸縮——它本來就是一團化開的光，多一行少一行都落在同一片淡底上。
   * 不直接刪是因為十來支頁面都在傳，留著讓呼叫端不必一起改；新頁面不需要指定。
   */
  band?: "short" | "default" | "tall";
  /** 返回行為。預設 navigate(-1)；傳 null 則不顯示返回鍵。 */
  onBack?: (() => void) | null;
  children: ReactNode;
}

const SkinV2Screen = ({ title, subHeader, headerRight, band = "default", onBack, children }: SkinV2ScreenProps) => {
  const navigate = useNavigate();
  const topBarRef = useRef<HTMLDivElement>(null);
  const [topBarH, setTopBarH] = useState(0);

  useLayoutEffect(() => {
    const el = topBarRef.current;
    if (!el) return;
    const measure = () => setTopBarH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  void band; // 見上：淡霧頁首不再依 subHeader 高度換長度
  const showBack = onBack !== null;

  return (
    <View height="100dvh" width="100vw" backgroundColor="#f8fafc">
      <View flex={1} position="relative" className="gas-skin-v2-bg">
        <div
          ref={topBarRef}
          className="gas-skin-v2-topbar"
          style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 5, padding: "14px 16px 10px" }}>
          <XStack justifyContent="space-between" alignItems="center">
            <XStack alignItems="center" flex={1} minWidth={0}>
              {showBack && (
                <View mr="$2" cursor="pointer" onPress={() => (onBack ? onBack() : navigate(-1))}>
                  <RiArrowLeftSLine size={20} color={v2.hazeInk} />
                </View>
              )}
              <TextCustom str={title} type={TextSize.SUBNORMAL} color={v2.hazeInk} bold={true} />
            </XStack>
            {headerRight}
          </XStack>
        </div>
        <RadiusContentWrapper background="transparent" contentContainerStyle={{ paddingTop: Math.max(topBarH - 16, 0) }}>
          {/* 內容包成單一節點：RadiusContentWrapper 會在它的「直接子元素」之間塞 space="$6"（24px），
              subHeader 與內文若各自是一個子元素，中間就會多出一段誰也沒要的空白。 */}
          <YStack flex={1}>
            {/* subHeader 跟著內容捲走，不釘在標題列裡：捲動時不改變任何元素高度，就沒有重排 */}
            {subHeader}
            {children}
          </YStack>
        </RadiusContentWrapper>
      </View>
    </View>
  );
};

export default SkinV2Screen;
